import { useEffect, useState } from 'react';

export interface VideoMetadata {
  duration: number; // in seconds
  size: number; // in bytes
  url: string;
}

/**
 * Fetch video metadata (duration, size) from a video URL.
 * Uses HTML5 video element to get duration and fetch API to get content-length.
 */
export function useVideoMetadata(url: string | null): {
  metadata: VideoMetadata | null;
  loading: boolean;
  error: string | null;
} {
  const [metadata, setMetadata] = useState<VideoMetadata | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setMetadata(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchMetadata = async () => {
      try {
        // Create video element to extract duration
        const video = document.createElement('video');
        // Ensure cross-origin is anonymous so metadata can be read when the server
        // provides proper CORS headers (Access-Control-Allow-Origin).
        // Without this, some browsers will block reading media metadata for
        // cross-origin resources.
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        video.muted = true;

        const durationPromise = new Promise<number>((resolve, reject) => {
          video.onloadedmetadata = () => {
            resolve(video.duration);
          };
          video.onerror = () => {
            reject(new Error('Failed to load video metadata'));
          };
          // Timeout after 10 seconds
          setTimeout(() => reject(new Error('Video metadata timeout')), 10000);
        });

        video.src = url;

        // First, try to fetch Firebase Storage object metadata JSON (if this URL
        // is a Storage download URL). Many Firebase download URLs include
        // `alt=media&token=...` — removing those query params returns a JSON
        // metadata endpoint which contains `size`.
        let sizeFromMetadata: number | null = null;
        try {
          const parsed = new URL(url);
          // Remove common query params used for downloading the raw file
          // (alt=media, token=...). The resulting URL often returns JSON
          // metadata for the object.
          parsed.searchParams.delete('alt');
          parsed.searchParams.delete('token');

          // Only attempt the metadata fetch for Firebase Storage hostnames
          if (parsed.hostname.includes('firebasestorage.googleapis.com')) {
            const metaResp = await fetch(parsed.toString(), { method: 'GET', mode: 'cors', credentials: 'omit' });
            if (metaResp.ok) {
              try {
                const metaJson = await metaResp.json();
                if (metaJson?.size) {
                  sizeFromMetadata = parseInt(metaJson.size, 10);
                }
              } catch (e) {
                // JSON parse failed — ignore and fall back to HEAD
              }
            }
          }
        } catch (e) {
          // URL parsing/fetch failed — ignore and fall back to HEAD below
        }

        // Fetch file size using HEAD request as a fallback if the metadata
        // endpoint did not provide a size.
        const sizePromise = (async () => {
          if (sizeFromMetadata !== null) return sizeFromMetadata;
          try {
            const resp = await fetch(url, { method: 'HEAD', mode: 'cors', credentials: 'omit' });
            const contentLength = resp.headers.get('content-length');
            return contentLength ? parseInt(contentLength, 10) : 0;
          } catch (err) {
            return 0;
          }
        })();

        const [duration, size] = await Promise.all([durationPromise, sizePromise]);

        if (!cancelled) {
          setMetadata({ duration, size, url });
          // Helpful debug info when metadata is missing
          if (size === 0) console.debug('useVideoMetadata: size unavailable for', url);
          if (!duration || Number.isNaN(duration)) console.debug('useVideoMetadata: duration unavailable for', url);
          setLoading(false);
        }

        // Clean up
        video.src = '';
        video.remove();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch metadata');
          setLoading(false);
        }
      }
    };

    fetchMetadata();

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { metadata, loading, error };
}

/**
 * Fetch metadata for multiple video URLs.
 */
export function useMultipleVideoMetadata(urls: string[]): {
  metadataList: (VideoMetadata | null)[];
  loading: boolean;
} {
  const [metadataList, setMetadataList] = useState<(VideoMetadata | null)[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!urls || urls.length === 0) {
      setMetadataList([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchAll = async () => {
      const results = await Promise.all(
        urls.map(async (url) => {
          try {
            // Create video element to extract duration
            const video = document.createElement('video');
            video.crossOrigin = 'anonymous';
            video.preload = 'metadata';
            video.muted = true;

            // Set the src before waiting for metadata so the browser begins
            // loading the media and will fire `onloadedmetadata`.
            video.src = url;
            const duration = await new Promise<number>((resolve, reject) => {
              let timedOut = false;
              const to = setTimeout(() => {
                timedOut = true;
                reject(new Error('Timeout'));
              }, 10000);

              video.onloadedmetadata = () => {
                if (timedOut) return;
                clearTimeout(to);
                resolve(video.duration);
              };
              video.onerror = () => {
                if (timedOut) return;
                clearTimeout(to);
                reject(new Error('Failed to load video metadata'));
              };
            });

            // Fetch file size
            let size = 0;
            try {
              // Try the Storage metadata JSON endpoint first (same logic as above)
              const parsed = new URL(url);
              parsed.searchParams.delete('alt');
              parsed.searchParams.delete('token');
              if (parsed.hostname.includes('firebasestorage.googleapis.com')) {
                const m = await fetch(parsed.toString(), { method: 'GET', mode: 'cors', credentials: 'omit' });
                if (m.ok) {
                  const j = await m.json();
                  if (j?.size) size = parseInt(j.size, 10);
                }
              }
            } catch (e) {
              // ignore
            }

            if (!size) {
              try {
                const response = await fetch(url, { method: 'HEAD', mode: 'cors', credentials: 'omit' });
                const contentLength = response.headers.get('content-length');
                size = contentLength ? parseInt(contentLength, 10) : 0;
              } catch (e) {
                size = 0;
              }
            }

            video.src = '';
            video.remove();

            return { duration, size, url };
          } catch (err) {
            return null;
          }
        })
      );

      if (!cancelled) {
        setMetadataList(results);
        setLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [urls.join(',')]);

  return { metadataList, loading };
}
